import React, { useState, useEffect } from "react";
import {
  DollarSign,
  User,
  Calendar,
  FileText,
  CheckCircle,
  Loader2,
  CreditCard,
  ClipboardList,
} from "lucide-react";
import { payoutTransactionService } from "../../lib/payoutTransactionService";
import { formatCurrency } from "../../utils/helpers";
import Card from "../ui/Card";
import Button from "../ui/Button";
import Alert from "../ui/Alert";

const PAYMENT_METHODS = [
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "paypal", label: "PayPal" },
  { value: "wise_manual", label: "Wise (Manual)" },
  { value: "venmo", label: "Venmo" },
  { value: "zelle", label: "Zelle" },
  { value: "crypto", label: "Crypto" },
  { value: "other", label: "Other" },
];

const ManualPayoutRecorder = ({ onPayoutRecorded }) => {
  const [affiliates, setAffiliates] = useState([]);
  const [allOpenRequests, setAllOpenRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Form state
  const [selectedAffiliate, setSelectedAffiliate] = useState("");
  const [checkedRequestIds, setCheckedRequestIds] = useState([]);
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("bank_transfer");
  const [notes, setNotes] = useState("");
  const [paidThroughDate, setPaidThroughDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  // Load affiliates on mount
  useEffect(() => {
    loadAffiliates();
  }, []);

  const loadAffiliates = async () => {
    setLoading(true);
    try {
      // Load balances + open requests together so the owed numbers include
      // amounts reserved by open payout_transaction rows (pending/approved/
      // processing), not just the unreserved balance.
      const [affiliatesResult, openResult] = await Promise.all([
        payoutTransactionService.getAffiliatesForPayout(),
        payoutTransactionService.getOpenPayoutRequests(),
      ]);

      if (affiliatesResult.success) {
        setAffiliates(affiliatesResult.data || []);
      } else {
        setError(affiliatesResult.error);
      }

      if (openResult.success) {
        setAllOpenRequests(openResult.data || []);
      } else {
        setError(openResult.error);
      }
    } catch (err) {
      setError("Failed to load affiliates");
    } finally {
      setLoading(false);
    }
  };

  // Open requests for the selected affiliate + reserved totals per affiliate
  const openRequests = allOpenRequests.filter(
    (request) => request.affiliate_name === selectedAffiliate
  );

  const openTotalsByAffiliate = allOpenRequests.reduce((totals, request) => {
    totals[request.affiliate_name] =
      (totals[request.affiliate_name] || 0) + (parseFloat(request.amount) || 0);
    return totals;
  }, {});

  const handleAffiliateChange = (e) => {
    const name = e.target.value;
    setSelectedAffiliate(name);
    // Prefill the loose amount with the unreserved balance; open requests
    // are handled by the checklist below.
    const affiliateData = affiliates.find((a) => a.affiliate_name === name);
    setAmount(
      name && affiliateData
        ? (parseFloat(affiliateData.total_unpaid) || 0).toFixed(2)
        : ""
    );
    // Requests default UNCHECKED — settling one is an explicit opt-in.
    setCheckedRequestIds([]);
  };

  const toggleRequestChecked = (requestId) => {
    setCheckedRequestIds((prev) =>
      prev.includes(requestId)
        ? prev.filter((id) => id !== requestId)
        : [...prev, requestId]
    );
  };

  const checkedRequests = openRequests.filter((r) =>
    checkedRequestIds.includes(r.id)
  );
  const checkedTotal = checkedRequests.reduce(
    (sum, r) => sum + (parseFloat(r.amount) || 0),
    0
  );
  const looseAmount = parseFloat(amount) || 0;
  const totalToRecord = checkedTotal + looseAmount;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!selectedAffiliate) {
      setError("Please select an affiliate");
      return;
    }

    const parsedAmount = amount === "" ? 0 : parseFloat(amount);
    if (Number.isNaN(parsedAmount) || parsedAmount < 0) {
      setError("Please enter a valid amount");
      return;
    }

    if (parsedAmount === 0 && checkedRequestIds.length === 0) {
      setError("Enter an amount or check at least one open request to settle");
      return;
    }

    setSubmitting(true);

    try {
      const result = await payoutTransactionService.recordManualPayout(
        selectedAffiliate,
        parsedAmount,
        paymentMethod,
        notes,
        paidThroughDate,
        checkedRequestIds
      );

      if (result.success) {
        const settledNote =
          checkedRequests.length > 0
            ? ` (${checkedRequests.length} open request${checkedRequests.length === 1 ? "" : "s"} settled)`
            : "";
        setSuccess(
          `Successfully recorded ${formatCurrency(parsedAmount + checkedTotal)} payout to ${selectedAffiliate}${settledNote}`
        );
        // Reset form
        setSelectedAffiliate("");
        setAmount("");
        setNotes("");
        setCheckedRequestIds([]);
        // Reload affiliates + open requests to show updated balances
        await loadAffiliates();
        // Notify parent if callback provided
        if (onPayoutRecorded) {
          onPayoutRecorded(result.data);
        }
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError("Failed to record payout");
    } finally {
      setSubmitting(false);
    }
  };

  const selectedAffiliateData = affiliates.find(
    (a) => a.affiliate_name === selectedAffiliate
  );
  const selectedOpenTotal = openRequests.reduce(
    (sum, r) => sum + (parseFloat(r.amount) || 0),
    0
  );

  if (loading) {
    return (
      <Card className="p-6 bg-gray-900/50 border border-gray-800">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
          <span className="ml-2 text-gray-400">Loading affiliates...</span>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 bg-gray-900/50 border border-gray-800">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-2 bg-green-500/20 rounded-lg">
          <DollarSign className="w-5 h-5 text-green-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-white">
            Record Manual Payout
          </h3>
          <p className="text-sm text-gray-400">
            Track payouts made outside the automated system
          </p>
        </div>
      </div>

      {error && <Alert type="error" message={error} />}

      {success && <Alert type="success" message={success} />}

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Affiliate Select */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            <User className="w-4 h-4 inline mr-2" />
            Affiliate
          </label>
          <select
            value={selectedAffiliate}
            onChange={handleAffiliateChange}
            className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          >
            <option value="">Select affiliate...</option>
            {affiliates.map((affiliate) => {
              const totalOwed =
                (parseFloat(affiliate.total_unpaid) || 0) +
                (openTotalsByAffiliate[affiliate.affiliate_name] || 0);
              return (
                <option
                  key={affiliate.affiliate_name}
                  value={affiliate.affiliate_name}
                >
                  {affiliate.affiliate_name}
                  {totalOwed > 0
                    ? ` (${formatCurrency(totalOwed)} owed)`
                    : " (paid up)"}
                </option>
              );
            })}
          </select>
        </div>

        {/* Show affiliate balance info when selected */}
        {selectedAffiliateData && (
          <div className="bg-gray-800/30 rounded-lg p-4 border border-gray-700/50">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-400">Unreserved Balance:</span>
                <span className="ml-2 text-white font-medium">
                  {formatCurrency(selectedAffiliateData.total_unpaid || 0)}
                </span>
              </div>
              <div>
                <span className="text-gray-400">In Open Requests:</span>
                <span className="ml-2 text-white font-medium">
                  {formatCurrency(selectedOpenTotal)}
                </span>
              </div>
              <div>
                <span className="text-gray-400">Total Owed:</span>
                <span className="ml-2 text-white font-medium">
                  {formatCurrency(
                    (parseFloat(selectedAffiliateData.total_unpaid) || 0) +
                      selectedOpenTotal
                  )}
                </span>
              </div>
              <div>
                <span className="text-gray-400">Last Payout:</span>
                <span className="ml-2 text-white font-medium">
                  {selectedAffiliateData.last_payout_date || "Never"}
                </span>
              </div>
              <div>
                <span className="text-gray-400">Lifetime Commission:</span>
                <span className="ml-2 text-white font-medium">
                  {formatCurrency(selectedAffiliateData.lifetime_commission || 0)}
                </span>
              </div>
              <div>
                <span className="text-gray-400">Lifetime Referral:</span>
                <span className="ml-2 text-white font-medium">
                  {formatCurrency(selectedAffiliateData.lifetime_referral || 0)}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Open payout requests checklist */}
        {selectedAffiliate && openRequests.length > 0 && (
          <div>
            <Alert
              type="warning"
              message={`This affiliate has open payout requests totaling ${formatCurrency(selectedOpenTotal)}. Check the ones your manual payment covered — otherwise they stay open.`}
            />
            <label className="block text-sm font-medium text-gray-300 mb-2">
              <ClipboardList className="w-4 h-4 inline mr-2" />
              Open Payout Requests
            </label>
            <div className="bg-gray-800/30 rounded-lg p-3 border border-gray-700/50 space-y-2">
              <p className="text-xs text-gray-500">
                Checked requests will be recorded as paid outside the system —
                no Wise transfer is sent.
              </p>
              {openRequests.map((request) => (
                <label
                  key={request.id}
                  className={`flex items-start gap-3 p-2 rounded-lg ${
                    request.settleable
                      ? "cursor-pointer hover:bg-white/5"
                      : "opacity-60 cursor-not-allowed"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checkedRequestIds.includes(request.id)}
                    disabled={!request.settleable}
                    onChange={() => toggleRequestChecked(request.id)}
                    className="mt-1 accent-green-600"
                  />
                  <div className="flex-1 text-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-white font-medium">
                        {formatCurrency(request.amount)}
                      </span>
                      <span className="text-xs text-gray-400 capitalize">
                        {request.status}
                      </span>
                    </div>
                    <div className="text-xs text-gray-500">
                      Requested{" "}
                      {new Date(request.request_date).toLocaleDateString()}
                    </div>
                    {!request.settleable && request.settle_block_reason && (
                      <div className="text-xs text-yellow-500 mt-1">
                        {request.settle_block_reason}
                      </div>
                    )}
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Amount */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            <DollarSign className="w-4 h-4 inline mr-2" />
            {openRequests.length > 0 ? "Additional Amount Paid" : "Amount Paid"}
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
              $
            </span>
            <input
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className="w-full bg-gray-800/50 border border-gray-700 rounded-lg pl-8 pr-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>
          {openRequests.length > 0 && (
            <p className="text-xs text-gray-500 mt-1">
              Loose amount beyond the checked requests above — may be 0 when
              only settling requests
            </p>
          )}
        </div>

        {/* Live total */}
        {selectedAffiliate &&
          (checkedRequestIds.length > 0 || looseAmount > 0) && (
            <div className="bg-gray-800/30 rounded-lg p-4 border border-gray-700/50 text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-gray-400">
                  Settled requests ({checkedRequests.length})
                </span>
                <span className="text-white">
                  {formatCurrency(checkedTotal)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Additional amount</span>
                <span className="text-white">
                  {formatCurrency(looseAmount)}
                </span>
              </div>
              <div className="flex justify-between font-medium border-t border-gray-700/50 pt-1">
                <span className="text-gray-300">Total recorded as paid</span>
                <span className="text-green-400">
                  {formatCurrency(totalToRecord)}
                </span>
              </div>
            </div>
          )}

        {/* Payment Method */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            <CreditCard className="w-4 h-4 inline mr-2" />
            Payment Method
          </label>
          <select
            value={paymentMethod}
            onChange={(e) => setPaymentMethod(e.target.value)}
            className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          >
            {PAYMENT_METHODS.map((method) => (
              <option key={method.value} value={method.value}>
                {method.label}
              </option>
            ))}
          </select>
        </div>

        {/* Paid Through Date */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            <Calendar className="w-4 h-4 inline mr-2" />
            Paid Through Date
          </label>
          <input
            type="date"
            value={paidThroughDate}
            onChange={(e) => setPaidThroughDate(e.target.value)}
            className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
          />
          <p className="text-xs text-gray-500 mt-1">
            Earnings after this date will be marked as unpaid
          </p>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            <FileText className="w-4 h-4 inline mr-2" />
            Notes (optional)
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="e.g., Transaction ID, reference number..."
            rows={2}
            className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent resize-none"
          />
        </div>

        {/* Submit */}
        <Button
          type="submit"
          disabled={
            submitting ||
            !selectedAffiliate ||
            (looseAmount <= 0 && checkedRequestIds.length === 0)
          }
          className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Recording...
            </>
          ) : (
            <>
              <CheckCircle className="w-4 h-4" />
              Record Payout
            </>
          )}
        </Button>
      </form>
    </Card>
  );
};

export default ManualPayoutRecorder;
